import React, { useEffect, useState } from 'react'
import { useStyles } from './style'
import { programService } from '../../Services'
import { connect } from 'react-redux'
import { AppButton, DisplayMessage, Loader } from '../../Components'
import { Popup } from '../../elements'
import { Formik, Form } from 'formik'
import * as yup from 'yup'
import { TextField, Box, FormControlLabel, Switch, Grid, Checkbox, Tooltip, Select, MenuItem } from '@material-ui/core'
import { InfoOutlined } from '@material-ui/icons'
import * as messageTypes from '../../utils/messageTypes'
import utilityFormFields from './constants'
import CloseIcon from '@material-ui/icons/Close'
import Breadcrumb from '../../Components/Breadcrumbs'

const CsipUtility = (props) => {
  const classes = useStyles();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [createUtility, setCreateUtility] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(messageTypes.SUCCESS);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [certificateFile, setCertificateFile] = useState(null);
  const [uploadingCertificate, setUploadingCertificate] = useState(false);
  const [allowedCountries, setAllowedCountries] = useState([]);
  const [initialFormValues, setInitialFormValues] = useState({
    profile: '',
    baseUrl: '',
    dcapPath: '',
    notificationBaseUrl: '',
    headerMediaType: '',
    derControlPollSeconds: 0,
    outOfBandPin: '',
    country: '',
    derControlPollerActive: false,
    fetchOnlyVirtualDerControls: false,
    enableNotification: false,
    penAsDecimal: false,
    matchUTCTick: false,
    nepkiCompliant: false,
    outOfBandPinValidationDisabled: false,
    mirrorReadingSetSupported: false,
    validateMMREligibility: false,
    clampOpmodeZeroToOne: false,
    passClientCertForServerCertValidation: false,
  });

  const resetFormState = () => {
    setIsEdit(false);
    setSelectedProfile(null);
    setCertificateFile(null);
    setInitialFormValues({
      profile: '',
      baseUrl: '',
      dcapPath: '',
      notificationBaseUrl: '',
      headerMediaType: '',
      derControlPollSeconds: 0,
      outOfBandPin: '',
      country: '',
      derControlPollerActive: false,
      fetchOnlyVirtualDerControls: false,
      enableNotification: false,
      penAsDecimal: false,
      matchUTCTick: false,
      nepkiCompliant: false,
      outOfBandPinValidationDisabled: false,
      mirrorReadingSetSupported: false,
      validateMMREligibility: false,
      clampOpmodeZeroToOne: false,
      passClientCertForServerCertValidation: false,
    });
  }

  const handleClose = () => {
    setCreateUtility(false);
    resetFormState();
  }

  const handleCreateNewUtility = () => {
    resetFormState();
    setCreateUtility(true);
  }

  const handleShowUtilities = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await programService.getCsipConfigurationDetails(props.accountId);
      if (data && Array.isArray(data.profileList)) {
        const filtered = data.profileList.slice(1);
        setProfiles(filtered);
      } else {
        setError('No profile list found.');
      }
      if (data && Array.isArray(data.allowedCountries)) {
        setAllowedCountries(data.allowedCountries);
      }
    } catch (e) {
      setError('Failed to fetch CSIP utilities.');
      setMessage('Failed to fetch CSIP utilities.');
      setMessageType(messageTypes.ERROR);
      setShowMessage(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    handleShowUtilities();
  }, []);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.name.endsWith('.zip')) {
        setCertificateFile(file);
      } else {
        setMessage('Please select a valid ZIP file');
        setMessageType(messageTypes.ERROR);
        setShowMessage(true);
        setCertificateFile(null);
      }
    }
    event.target.value = null;
  };

  const handleUploadCertificate = async () => {
    if (!certificateFile) {
      setMessage('Please select a certificate file first');
      setMessageType(messageTypes.ERROR);
      setShowMessage(true);
      return;
    }

    setUploadingCertificate(true);
    try {
      await programService.uploadCertificate(props.accountId, selectedProfile, certificateFile);
      setMessage('Certificate uploaded successfully');
      setMessageType(messageTypes.SUCCESS);
      setShowMessage(true);
    } catch (e) {
      setMessage('Failed to upload certificate');
      setMessageType(messageTypes.ERROR);
      setShowMessage(true);
    }
    setUploadingCertificate(false);
  };

  const handleEditUtility = async (profile) => {
    setLoading(true);
    setError(null);
    try {
      const data = await programService.getCsipUtilityByProfile(props.accountId, profile);
      if (data) {
        setInitialFormValues({
          profile: data.profile || profile,
          baseUrl: data.baseUrl || '',
          dcapPath: data.dcapPath || '',
          notificationBaseUrl: data.notificationBaseUrl || '',
          headerMediaType: data.headerMediaType || '',
          derControlPollSeconds: data.derControlPollSeconds || 60,
          outOfBandPin: data.outOfBandPin || '',
          country: data.country || '',
          derControlPollerActive: data.derControlPollerActive !== undefined ? data.derControlPollerActive : false,
          fetchOnlyVirtualDerControls: data.fetchOnlyVirtualDerControls !== undefined ? data.fetchOnlyVirtualDerControls : false,
          enableNotification: data.enableNotification !== undefined ? data.enableNotification : false,
          penAsDecimal: data.penAsDecimal !== undefined ? data.penAsDecimal : false,
          matchUTCTick: data.matchUTCTick !== undefined ? data.matchUTCTick : false,
          nepkiCompliant: data.nepkiCompliant !== undefined ? data.nepkiCompliant : false,
          outOfBandPinValidationDisabled: data.outOfBandPinValidationDisabled !== undefined ? data.outOfBandPinValidationDisabled : false,
          mirrorReadingSetSupported: data.mirrorReadingSetSupported !== undefined ? data.mirrorReadingSetSupported : false,
          validateMMREligibility: data.validateMMREligibility !== undefined ? data.validateMMREligibility : false,
          clampOpmodeZeroToOne: data.clampOpmodeZeroToOne !== undefined ? data.clampOpmodeZeroToOne : false,
          passClientCertForServerCertValidation: data.passClientCertForServerCertValidation !== undefined ? data.passClientCertForServerCertValidation : false,
          pausedTriggers: data.pausedTriggers || null
        });
        setSelectedProfile(profile);
        setIsEdit(true);
        setCreateUtility(true);
      }
    } catch (e) {
      setError('Failed to fetch profile details.');
      setMessage('Failed to fetch profile details.');
      setMessageType(messageTypes.ERROR);
      setShowMessage(true);
    }
    setLoading(false);
  };

  const handleSubmitUtility = async (values, { setSubmitting, resetForm }) => {
    setLoading(true);
    try {
      const payload = {
        profile: values.profile,
        baseUrl: values.baseUrl,
        dcapPath: values.dcapPath,
        notificationBaseUrl: values.notificationBaseUrl,
        headerMediaType: values.headerMediaType,
        derControlPollSeconds: values.derControlPollSeconds,
        outOfBandPin: values.outOfBandPin,
        country: values.country,
        derControlPollerActive: values.derControlPollerActive,
        fetchOnlyVirtualDerControls: values.fetchOnlyVirtualDerControls,
        enableNotification: values.enableNotification,
        penAsDecimal: values.penAsDecimal,
        matchUTCTick: values.matchUTCTick,
        nepkiCompliant: values.nepkiCompliant,
        outOfBandPinValidationDisabled: values.outOfBandPinValidationDisabled,
        mirrorReadingSetSupported: values.mirrorReadingSetSupported,
        validateMMREligibility: values.validateMMREligibility,
        clampOpmodeZeroToOne: values.clampOpmodeZeroToOne,
        passClientCertForServerCertValidation: values.passClientCertForServerCertValidation,
        pausedTriggers: values.pausedTriggers
      };
      
      await programService.createorUpdateCsipUtility(props.accountId, values.profile, payload);
      if (isEdit) {
        setMessage('CSIP Utility updated successfully');
      } else {
        setMessage('CSIP Utility created successfully');
      }

      setMessageType(messageTypes.SUCCESS);
      setShowMessage(true);
      setCreateUtility(false);
      setIsEdit(false);
      setSelectedProfile(null);
      setCertificateFile(null);
      resetForm();
      handleShowUtilities();
    } catch (e) {
      setMessage(isEdit ? 'Failed to update CSIP Utility' : 'Failed to create CSIP Utility');
      setMessageType(messageTypes.ERROR);
      setShowMessage(true);
    }
    setLoading(false);
    setSubmitting(false);
  };

  const validationSchema = React.useMemo(() => {
    return yup.object({
      profile: yup.string()
        .required('Profile name is required')
        .test('unique-profile', 'Profile name already exists', function(value) {
          if (isEdit) {
            return true;
          }
          if (!value) {
            return true;
          }
          const lowerCaseProfiles = profiles.map(p => p.toLowerCase());
          const isDuplicate = lowerCaseProfiles.includes(value.toLowerCase());
          return !isDuplicate;
        }),
      baseUrl: yup.string().required('Base URL is required'),
      dcapPath: yup.string().required('dcapPath is required'),
      headerMediaType: yup.string().required('Header Media Type is required'),
      derControlPollSeconds: yup.number().required('Poll Seconds is required').positive().integer(),
      outOfBandPin: yup.string()
        .max(10, 'Out of Band Pin must be at most 10 digits')
        .matches(/^\d+$/, 'Out of Band Pin must contain only numbers'),
    });
  }, [profiles, isEdit]);

  const renderFormContent = () => {
    return (
      <Formik
        initialValues={initialFormValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={handleSubmitUtility}
      >
        {({ values, errors, touched, setFieldValue, isSubmitting }) => {
          return (
          <Form>
            <Box className={classes.formContentBox}>
              <div className={classes.formHeaderContainer}>
                <h1 className={classes.title}>{isEdit ? 'Edit Utility' : 'Create New Utility'}</h1>
                <span onClick={handleClose} className={classes.pointer}><CloseIcon className={classes.closeIcon} /></span>
              </div>
              <Grid container spacing={2} className={classes.formFieldsContainer}>
                {utilityFormFields && utilityFormFields.map((element, index) => (
                  (element.type === 'text' || element.type === 'number') ? (
                    <Grid item xs={12} key={element.id}>
                      <Grid container alignItems="center">
                        <Grid item xs={4}>
                          {element.label}
                        </Grid>
                        <Grid item xs={8}>
                          <TextField
                            fullWidth
                            id={element.id}
                            name={element.name}
                            value={values[element.id]}
                            onChange={(e) => setFieldValue(element.id, e.target.value.trim())}
                            error={touched[element.id] && Boolean(errors[element.id])}
                            helperText={touched[element.id] && errors[element.id]}
                            variant="outlined"
                            size='small'
                            disabled={isEdit && element.id === 'profile'}
                            InputProps={isEdit && element.id === 'profile' ? { readOnly: true } : {}}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    ) : element.type === 'dropdown' ? (
                      <Grid item xs={12} key={element.id}>
                        <Grid container alignItems="center">
                          <Grid item xs={4}>
                            {element.label}
                          </Grid>
                          <Grid item xs={8}>
                            <Select
                              fullWidth
                              id={element.id}
                              name={element.name}
                              value={values[element.id] || ''}
                              onChange={(e) => setFieldValue(element.id, e.target.value)}
                              variant="outlined"
                              size='small'
                            >
                              {allowedCountries.map((country) => (
                                <MenuItem key={country} value={country}>
                                  {country === '' ? 'Empty (Disable DNSP Connector)' : country}
                                </MenuItem>
                              ))}
                            </Select>
                          </Grid>
                        </Grid>
                      </Grid>
                    ) : element.type === 'switch' ? (
                      <Grid item xs={12}>
                        <Grid container alignItems="center" spacing={2}>
                          <Grid item xs={8}>
                            {element.label}
                          </Grid>
                          <Grid item xs={4}>
                            <Checkbox
                              checked={values[element.id]}
                              onChange={(e) => setFieldValue(element.id, e.target.checked)}
                              name={element.name}
                              color="primary"
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    ) : null
                  ))}
              </Grid>
              
              {isEdit && (
                <Box className={classes.certificateUploadBox}>
                  <h3 className={classes.certificateTitle}>
                    Upload Certificate
                    <Tooltip
                      title={
                        <div className={classes.tooltipContent}>
                          <div>Elliptic Curve (EC) keys needs to be mentioned in ec_key.pem</div>
                          <div>PKCS#8 format keys needs to be mentioned in key.pem</div>
                          <div>One of these two files needs to be present</div>
                        </div>
                      }
                      arrow
                      placement="right"
                      classes={{ tooltip: classes.whiteTooltip, arrow: classes.whiteTooltipArrow }}
                    >
                      <InfoOutlined className={classes.infoIcon} />
                    </Tooltip>
                  </h3>
                  <p className={classes.certificateNote}>
                    <strong>Note:</strong> Upload a zip file (max 10MB) that contains key.pem (or ec_key.pem), fullchain.pem (if available), certificate.pfx (or certificate.pem).
                  </p>
                  <Grid container spacing={2} className={classes.uploadButtonsContainer}>
                    <Grid item xs={3}>
                      <input
                        type="file"
                        accept=".zip"
                        onChange={handleFileSelect}
                        className={classes.hiddenFileInput}
                        id="certificate-file-input"
                      />
                      <label htmlFor="certificate-file-input" className={classes.fileInputLabel}>
                        <AppButton
                          text={certificateFile ? certificateFile.name : "Select ZIP File"}
                          fill={false}
                          component="span"
                          className={classes.zipWrapper}
                        />
                      </label>
                    </Grid>
                    <Grid item xs={3}>
                      <AppButton
                        text="Upload"
                        onClick={handleUploadCertificate}
                        fill={true}
                        disabled={!certificateFile || uploadingCertificate}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
              
              <Box className={classes.formButtonsContainer}>
                <AppButton
                  text="Cancel"
                  onClick={handleClose}
                  fill={false}
                  className={`${classes.mr4} ${classes.buttons} ${classes.innerContainer}`} 
                />
                <AppButton
                  text="Submit"
                  type="submit"
                  disabled={isSubmitting}
                  fill={true}
                  className={`${classes.mr4} ${classes.buttons} ${classes.innerContainer}`} 
                />
              </Box>
            </Box>
          </Form>
        );
        }}
      </Formik>
    );
  };

  return (
    <div className={classes.tableWrapper}>
      <Breadcrumb />
      <div className={classes.headerWrapper}>
        <h1 className={classes.title}>Onboard Csip Utility</h1>
        <AppButton className={`${classes.mr4} ${classes.buttons}`} text="Create New Utility" onClick={handleCreateNewUtility} fill={false}/>
      </div>
      {
        <table className={classes.table}>
          <thead>
            <tr>
              <th>SR NO.</th>
              <th>PROFILE</th>
            </tr>
          </thead>
          <tbody>
            {profiles.map((profile, idx) => (
              <tr key={profile}>
                <td>{idx + 1}</td>
                <td>
                  <span 
                    onClick={() => handleEditUtility(profile)} 
                    className={classes.profileLink}
                  >
                    {profile}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      }
      <Popup
        open={createUtility}
        handleClose={handleClose}
        content={renderFormContent()}
        className={classes.widePopup}
      />
      <Loader loading={loading} />
      <DisplayMessage
        open={showMessage}
        message={message}
        messageType={messageType}
        handleClose={() => setShowMessage(false)}
      />
    </div>
  );
}

const mapStateToProps = ({ auth }) => {
  const { isSuperAdmin, accountId } = auth
  return { isSuperAdmin, accountId }
}

export default connect(mapStateToProps)(CsipUtility);